import * as React from "react";
import uuid from "react-native-uuid";
import {
  View,
  StyleSheet,
  Dimensions,
  ScrollView,
  TouchableOpacity,
  Alert,
  Modal,
  TextInput,
  TouchableWithoutFeedback,
  ToastAndroid,
} from "react-native";
import Text from "@components/CustomText";
import { fetchTicketDetails, sendReply } from "@services/api";
import HTML from "react-native-render-html";
import { format } from "date-fns";
import { useIsFocused } from "@react-navigation/native";
import CameraIcon from "@assets/svg/Cameraicon";
import { launchCamera } from "@utils/imagePickerCompat";
import t from "@constants/localization";
import Button from "../components/Form/Button";
import Loader from "../components/CoverLoader";
import CTAButton from "./CTAButton";
import MessageInput from "./MessageInput";
import CancelOrReopenDialog from "./CancelOrReopenDialog";
import Conversation from "./Conversation";
import FullInfo from "./FullInfo";
import RatingBottomView from "./RatingBottomView";
import cancelSupportTicket from "../utils/cancelSupportTicket";
import reopenSupportTicket from "../utils/reopenSupportTicket";
import colors from "@constants/color";

// import { uploadImage } from "@services/api";
import checkPermissions from "../components/Form/Images/checkPermissions";
import uploadImage from "../utils/uploadImage";

const PATH = "screens/SupportTree/SupportTickets/SupportTicket";

const colorMap = {
  कैंसिल्ड: "#C41A16",
  साल्व्ड: "#55CD1D",
  प्रोसेसिंग: "#F25700",
};
const SupportTicketItem = ({ navigation, route }) => {
  const [ticketDetails, setTicketDetails] = React.useState();
  const [showDetails, setShowDetails] = React.useState(false);
  const [fetching, setFetching] = React.useState(true);
  const [showDialog, setShowDialog] = React.useState(false);
  const [dialogProps, setDialogProps] = React.useState({ visible: false });

  const [displayTab, setDisplayTab] = React.useState("conversation");
  const [reply, setReply] = React.useState(null);
  const [attachment, setAttachment] = React.useState(null);
  const [openAttach, setOpenAttach] = React.useState(false);
  const scrollRef = React.useRef(null);
  const focused = useIsFocused();
  const [isFirstLoad, setIsFirstLoad] = React.useState(true);

  const [srcMap, setSrcMap] = React.useState([]);
  const [sendBtnActive, setSendBtnActive] = React.useState(true);

  const remove = (id) => {
    const itemIdx = srcMap.findIndex((src) => src.id === id);
    if (itemIdx !== -1) {
      srcMap.splice(itemIdx, 1);
      setSrcMap(srcMap.slice());
    }
  };

  const updateSrc = (newMap) => {
    setSrcMap([...srcMap, newMap]);
  };

  const updateUrlRef = React.useRef();
  updateUrlRef.current = (id, url) => {
    const itemIdx = srcMap.findIndex((s) => s.id === id);
    if (itemIdx !== -1) {
      srcMap[itemIdx] = {
        ...srcMap[itemIdx],
        url,
      };
      setSrcMap(srcMap.slice());
    }
  };

  const onPressRight = (type) => (ticketDetails) => {
    if (type === "cancel") {
      setDialogProps({
        visible: true,
        title: t(`${PATH}/cancelTicket`),
        message: t(`${PATH}/cnfmTicketCancellation`),
        buttons: [
          {
            text: t(`${PATH}/no`),
            onPress: () => setDialogProps((s) => ({ ...s, visible: false })),
            style: "cancel",
          },
          {
            text: t(`${PATH}/yes`),
            onPress: () => {
              cancelSupportTicket(
                ticketDetails.ticket_id,
                ticketDetails.cf_issue_level_1,
                () => {
                  setDialogProps((s) => ({ ...s, visible: false }));
                  setFetching(true);
                  navigation.navigate("SupportTree");
                },
              );
            },
            style: "okay",
          },
        ],
      });
    } else if (type === "reopen") {
      setDialogProps({
        visible: true,
        title: t(`${PATH}/reopenTicket`),
        message: t(`${PATH}/cnfmTicketReopening`, {
          ticketId: ticketDetails.ticket_id,
        }),
        buttons: [
          {
            text: t(`${PATH}/no`),
            onPress: () =>
              reopenSupportTicket(
                "cancel",
                ticketDetails.ticket_id,
                ticketDetails.cf_issue_level_1,
              )(() => {
                setDialogProps((s) => ({ ...s, visible: false }));
                setFetching(true);
              }),
            style: "cancel",
          },
          {
            text: t(`${PATH}/yes`),
            onPress: () =>
              reopenSupportTicket(
                "okay",
                ticketDetails.ticket_id,
                ticketDetails.cf_issue_level_1,
              )(() => {
                setDialogProps((s) => ({ ...s, visible: false }));
                setFetching(true);
              }),
            style: "okay",
          },
        ],
      });
    }
  };

  React.useEffect(() => {
    if (
      scrollRef &&
      scrollRef.current &&
      typeof scrollRef.current.scrollToEnd === "function"
    ) {
      scrollRef.current.scrollToEnd({ animated: true });
    }
  }, [scrollRef.current]);

  React.useEffect(() => {
    if (focused) {
      (async function () {
        try {
          const { ticketId } = route && route.params;
          const { data: ticketDetails } = await fetchTicketDetails(ticketId);
          setTicketDetails(ticketDetails);
          navigation.setParams({
            headerTitle: t(`${PATH}/ticketDetails`),
            numberOfLines: 2,
            headerStyle: { width: "80%" },
          });
          navigation.setOptions({
            headerRight: () =>
              !ticketDetails.resolved_at && !ticketDetails.cancelled_at ? (
                <TouchableOpacity
                  onPress={() => onPressRight("cancel")(ticketDetails)}
                >
                  <View>
                    <Text variant="body_text_4" style={{ color: "#E40046" }}>
                      {t(`${PATH}/cancelTicket`)}
                    </Text>
                  </View>
                </TouchableOpacity>
              ) : null,
          });

          setFetching(false);
          setIsFirstLoad(false);
        } catch (e) {
          console.log(e);
          navigation.pop();
        }
      })();
    }
  }, [fetching, setFetching, focused, setIsFirstLoad]);

  if (fetching && isFirstLoad) {
    return <Loader />;
  }

  const renderTab = (type) => {
    if (type === "full_info") return <FullInfo ticketDetails={ticketDetails} />;
    else if (type === "conversation") {
      return (
        <>
          <Conversation ticketDetails={ticketDetails} />
        </>
      );
    }
  };

  const getRequestPayload = (reply, attachments = []) => {
    let html;
    if (attachments.length) {
      html = `<div> <div> ${reply} </div> `;
      attachments.forEach((url) => {
        if (url) {
          html = `${html} 
                    <div> 
                      <img style="margin: 5px 0; height: 200px;" src="${url}" /> 
                    </div> 
                  </div>`;
        }
      });
    } else html = `<div>${reply} </div>`;
    return html;
  };

  const onSendMessage = async (func) => {
    if (
      (reply && typeof reply === "string" && reply.trim() !== "") ||
      (srcMap && srcMap.length > 0)
    ) {
      const attachments = srcMap.map((s) => s.url);

      const { ticket_id } = ticketDetails;
      const body = {
        reply: srcMap.length
          ? reply
            ? getRequestPayload(reply.trim(), attachments)
            : getRequestPayload("", attachments)
          : getRequestPayload(reply.trim()),
        ticket_id,
      };
      setSendBtnActive(false);
      try {
        const { data: responseData } = await sendReply(body);
        setReply(null);
        setAttachment(null);
        setSrcMap([]);
        if (func && typeof func === "function") func();
        const { ticketId } = route && route.params;
        const { data } = await fetchTicketDetails(ticketId);
        setTicketDetails(data);
        if (
          scrollRef &&
          scrollRef.current &&
          typeof scrollRef.current.scrollToEnd === "function"
        ) {
          scrollRef.current.scrollToEnd({ animated: false });
        }
        setSendBtnActive(true);
      } catch (err) {
        setSendBtnActive(true);
        console.log("error response", err);
      }
    }
  };

  const openCamera = () => {
    launchCamera(
      {
        mediaType: "photo",
        maxHeight: 2048,
        quality: 0.4,
      },
      onImageSelect,
    );
  };
  const onImageSelect = async (response) => {
    try {
      if (response.didCancel) {
        return;
      } else if (response.error) {
        console.log("ImagePicker Error: ", response.error);
      }
      const source = { uri: `${response.uri}` };
      const id = String(uuid.v1());
      updateSrc({ source, id });

      const data = new FormData();
      data.append("image", {
        name: response.fileName,
        type: response.type,
        uri: response.uri,
      });
      const { data: imageData } = await uploadImage(data);
      updateUrlRef.current(id, imageData.url);

      setOpenAttach(false);
    } catch (e) {
      setOpenAttach(false);
      console.log("upload image rror", e);
      // show upload image error
      ToastAndroid.showWithGravity(
        t(`${PATH}/imageUploadError`),
        ToastAndroid.SHORT,
        ToastAndroid.CENTER,
      );
    }
  };

  return ticketDetails ? (
    <>
      {ticketDetails.cancelled_at ||
      ticketDetails.resolved_at ? null : displayTab === "conversation" ? (
        <MessageInput
          message={reply}
          onChangeText={setReply}
          attachments={srcMap}
          removeImage={remove}
          onSendMessage={onSendMessage}
          active={
            ((reply && reply.trim() !== "") ||
              (srcMap?.length && !srcMap?.some((el) => !el?.url))) &&
            sendBtnActive
          }
          onClickAttachment={async () => {
            try {
              await checkPermissions();
              if (srcMap.length >= 3) {
                ToastAndroid.showWithGravity(
                  t(`${PATH}/canOnlySend3Images`),
                  ToastAndroid.SHORT,
                  ToastAndroid.CENTER,
                );
                return;
              }
              setOpenAttach((s) => !s);
            } catch (err) {
              console.log(err);
            }
          }}
          onCloseAttachment={() => setOpenAttach(false)}
        />
      ) : null}
      {openAttach && (
        <OpenAttachmentModal
          openCamera={openCamera}
          closeModal={() => setOpenAttach(false)}
          attachments={srcMap}
        />
      )}

      <ScrollView
        ref={scrollRef}
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{
          flexGrow: 1,
          position: "relative",
          paddingBottom: 70,
          backgroundColor: "white",
        }}
      >
        <CancelOrReopenDialog
          visible={dialogProps.visible}
          onRequestClose={() =>
            setDialogProps((s) => ({ ...s, visible: false }))
          }
          title={dialogProps.title || ""}
          message={dialogProps.message || ""}
          buttons={dialogProps.buttons || []}
        />
        <View style={style.wrapper}>
          <Text variant="body_text_4" style={style.subject}>
            {ticketDetails.subject}
          </Text>

          <Text
            variant="body_text_3"
            style={{ color: "rgba(0,0,0,0.56)", marginVertical: 12 }}
          >
            {t(`${PATH}/ticketNo`)} : {ticketDetails.ticket_id} •{" "}
            {format(new Date(ticketDetails.created_at), "do MMM, hh:mm aaaa")}
          </Text>

          <Text
            variant="body_text_3"
            style={{ color: "rgba(0,0,0,0.56)", marginBottom: 12 }}
          >
            {t(`${PATH}/status`)} :{" "}
            <Text
              variant="body_text_4"
              style={{ color: colorMap[ticketDetails.app_status] }}
            >
              {ticketDetails.app_status}
            </Text>{" "}
            {ticketDetails.cancelled_at && (
              <Text variant="body_text_3" style={{ color: "rgba(0,0,0,0.56)" }}>
                •{" "}
                {format(
                  new Date(ticketDetails.cancelled_at),
                  "do MMM, hh:mm aaaa",
                )}
              </Text>
            )}
          </Text>
        </View>
        <View
          style={{
            paddingVertical: 15,
            backgroundColor: "#efefef",
          }}
        >
          {ticketDetails.cf_order_id ? (
            <View
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                paddingHorizontal: 20,
              }}
            >
              <CTAButton
                width="45%"
                btnText={t(`${PATH}/statusDescription`)}
                active={displayTab === "conversation"}
                btnTextColor="rgba(0, 0, 0, 0.86)"
                activeBtnTextColor="#E40046"
                onPress={() => setDisplayTab("conversation")}
                btnStyle={{
                  borderColor: "rgba(0, 0, 0, 0.2)",
                  backgroundColor: "white",
                }}
                activeBtnStyle={{
                  borderColor: "#E40046",
                  backgroundColor: "rgba(228, 0, 70, 0.1)",
                }}
              />
              <CTAButton
                onPress={() => setDisplayTab("full_info")}
                width="45%"
                btnText={t(`${PATH}/fullInfo`)}
                btnTextColor="rgba(0, 0, 0, 0.86)"
                btnStyle={{
                  borderColor: "rgba(0, 0, 0, 0.2)",
                  backgroundColor: "white",
                }}
                active={displayTab === "full_info"}
                activeBtnTextColor="#E40046"
                activeBtnStyle={{
                  borderColor: "#E40046",
                  backgroundColor: "rgba(228, 0, 70, 0.1)",
                }}
              />
            </View>
          ) : (
            <></>
          )}
        </View>
        {renderTab(displayTab)}
      </ScrollView>

      {ticketDetails.resolved_at && !ticketDetails.cancelled_at && (
        <RatingBottomView
          ticket_id={ticketDetails.ticket_id}
          givenRating={ticketDetails.rating}
          ratingDate={ticketDetails.rating_date}
          handleReload={() => setFetching(true)}
          resolved_at={ticketDetails.resolved_at}
          issueLevel1={ticketDetails.cf_issue_level_1}
          showReopenModalHandler={() => onPressRight("reopen")(ticketDetails)}
        />
      )}
    </>
  ) : null;
};

const style = StyleSheet.create({
  wrapper: {
    marginTop: 10,
    paddingHorizontal: 20,
    backgroundColor: "white",
    paddingVertical: 10,
  },
  subject: {
    color: "black",
    paddingVertical: 5,
  },
  cancel: {
    color: "rgba(0, 0, 0, 0.56)",
    marginRight: 40,
  },
  okay: { color: "#E40046" },
  attachmentModal: {
    position: "absolute",
    backgroundColor: "white",
    borderRadius: 10,
    borderWidth: 0.5,
    borderColor: "rgba(0, 0, 0, 0.1)",
    left: 25,
    zIndex: 9,
  },
  cameraIconStyle: {
    backgroundColor: "#FF7A00",
    borderRadius: 30,
    padding: 10,
  },
  wrapperView: {
    flexDirection: "row",
    justifyContent: "space-between",
    flexWrap: "wrap",
    flex: 1,
    marginBottom: 10,
  },
  ticketNumberWithDate: {
    color: colors.greyTone1,
    marginBottom: 10,
    marginLeft: -1,
  },
});

export default SupportTicketItem;

const OpenAttachmentModal = ({
  openCamera = () => {},
  closeModal = () => {},
  attachments = [],
}) => (
  <Modal
    onRequestClose={closeModal}
    transparent
    statusBarTranslucent
    navigationBarTranslucent
  >
    <View style={{ flex: 1 }}>
      <TouchableWithoutFeedback onPress={closeModal}>
        <View style={{ flex: 1 }} />
      </TouchableWithoutFeedback>
      <View
        style={[
          style.attachmentModal,
          { bottom: attachments.length ? 160 : 80 },
        ]}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            paddingHorizontal: 15,
          }}
        >
          <View style={{ margin: 10, padding: 5, alignItems: "center" }}>
            <TouchableOpacity onPress={openCamera}>
              <View style={style.cameraIconStyle}>
                <CameraIcon />
              </View>
            </TouchableOpacity>
            <Text
              variant="caption_2"
              style={{ color: "rgba(0, 0, 0, 0.56)", marginTop: 5 }}
            >
              कैमरा{" "}
            </Text>
          </View>
        </View>
      </View>
    </View>
  </Modal>
);
